import React, { useEffect, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Grid,
    IconButton,
    MenuItem,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Typography,
    ToggleButton,
    ToggleButtonGroup
} from '@mui/material';
import { IconTrash, IconPlus } from '@tabler/icons';
import { useHistory } from 'react-router-dom';
import api from '../../../../services/api';

const formatCurrencyBR = (value) => {
    const number = Number(value);
    if (Number.isNaN(number)) return 'R$ 0,00';
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(number);
};

const CreatePurchaseRequest = () => {
    const history = useHistory();

    const [appId, setAppId] = useState(null);
    const [status, setStatus] = useState({ type: '', message: '' });

    const [justification, setJustification] = useState('');
    const [notes, setNotes] = useState('');

    const [products, setProducts] = useState([]);
    const [services, setServices] = useState([]);
    const [items, setItems] = useState([]);

    const [itemType, setItemType] = useState('produto');
    const [selectedProductId, setSelectedProductId] = useState('');
    const [selectedVariacaoId, setSelectedVariacaoId] = useState('');
    const [selectedServiceId, setSelectedServiceId] = useState('');
    const [quantidade, setQuantidade] = useState(1);

    useEffect(() => {
        (async () => {
            try {
                const res = await api.get('v1/apps/default/');
                setAppId(res.data.app.id);
            } catch (error) {
                console.error(error);
                setStatus({ type: 'error', message: 'Erro ao buscar app padrão' });
            }
        })();
    }, []);

    useEffect(() => {
        if (!appId) return;
        (async () => {
            try {
                const res = await api.get(`v1/apps/${appId}/products/`);
                setProducts(res.data || []);
            } catch (error) {
                console.error(error);
            }
        })();
    }, [appId]);

    useEffect(() => {
        if (!appId) return;
        (async () => {
            try {
                const res = await api.get(`v1/apps/${appId}/services/`);
                setServices(res.data.services || []);
            } catch (error) {
                console.error(error);
            }
        })();
    }, [appId]);

    const selectedProduct = products.find((p) => String(p.id) === String(selectedProductId));
    const variacoesDoProduto = selectedProduct?.variacoes || [];
    const selectedVariacao = variacoesDoProduto.find((v) => String(v.id) === String(selectedVariacaoId));
    const selectedService = services.find((s) => String(s.id) === String(selectedServiceId));

    const addItem = () => {
        const qtd = Number(quantidade);
        if (!qtd || qtd <= 0) {
            setStatus({ type: 'error', message: 'Quantidade deve ser maior que zero' });
            return;
        }

        if (itemType === 'produto') {
            if (!selectedVariacao) {
                setStatus({ type: 'error', message: 'Selecione um produto e uma variação' });
                return;
            }
            setItems((prev) => [
                ...prev,
                {
                    produto_variacao: selectedVariacao.id,
                    service: null,
                    nome: `${selectedProduct.nome} - ${selectedVariacao.nome_variacao}`,
                    quantidade: qtd,
                    valor_unitario_estimado: selectedVariacao.valor_custo
                }
            ]);
            setSelectedProductId('');
            setSelectedVariacaoId('');
        } else {
            if (!selectedService) {
                setStatus({ type: 'error', message: 'Selecione um serviço' });
                return;
            }
            setItems((prev) => [
                ...prev,
                {
                    produto_variacao: null,
                    service: selectedService.id,
                    nome: selectedService.name,
                    quantidade: qtd,
                    valor_unitario_estimado: selectedService.price
                }
            ]);
            setSelectedServiceId('');
        }

        setQuantidade(1);
        setStatus({ type: '', message: '' });
    };

    const removeItem = (index) => {
        setItems((prev) => prev.filter((_, i) => i !== index));
    };

    const totalEstimado = items.reduce((acc, item) => acc + Number(item.quantidade) * Number(item.valor_unitario_estimado), 0);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setStatus({ type: '', message: '' });

        if (!appId) {
            setStatus({ type: 'error', message: 'App não carregado ainda' });
            return;
        }
        if (items.length === 0) {
            setStatus({ type: 'error', message: 'Adicione ao menos um item à solicitação' });
            return;
        }

        try {
            const payload = {
                justification,
                notes,
                items: items.map((item) => ({
                    produto_variacao: item.produto_variacao || undefined,
                    service: item.service || undefined,
                    quantidade: item.quantidade,
                    valor_unitario: item.valor_unitario_estimado
                }))
            };
            const response = await api.post(`v1/apps/${appId}/purchase_requests/`, payload);
            history.push(`/dashboard/purchasing/requests/${response.data.id}/`);
        } catch (error) {
            console.error(error);
            setStatus({ type: 'error', message: error.response?.data?.error || 'Erro ao criar solicitação de compra' });
        }
    };

    return (
        <Box>
            <Typography variant="h3" gutterBottom sx={{ mb: 2 }}>
                Nova Solicitação de Compra
            </Typography>

            <Card>
                <CardContent>
                    {status.message && (
                        <Alert severity={status.type} sx={{ mb: 2 }}>
                            {status.message}
                        </Alert>
                    )}

                    <form onSubmit={handleSubmit}>
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <TextField
                                    label="Justificativa"
                                    fullWidth
                                    placeholder="Por que essa compra é necessária?"
                                    value={justification}
                                    onChange={(e) => setJustification(e.target.value)}
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <TextField
                                    label="Observações"
                                    fullWidth
                                    multiline
                                    minRows={2}
                                    value={notes}
                                    onChange={(e) => setNotes(e.target.value)}
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <Typography variant="h5" sx={{ mt: 2, mb: 1 }}>
                                    Itens
                                </Typography>
                            </Grid>

                            <Grid item xs={12}>
                                <ToggleButtonGroup
                                    size="small"
                                    exclusive
                                    value={itemType}
                                    onChange={(e, value) => {
                                        if (!value) return;
                                        setItemType(value);
                                        setSelectedProductId('');
                                        setSelectedVariacaoId('');
                                        setSelectedServiceId('');
                                    }}
                                >
                                    <ToggleButton value="produto">Produto</ToggleButton>
                                    <ToggleButton value="servico">Serviço</ToggleButton>
                                </ToggleButtonGroup>
                            </Grid>

                            {itemType === 'produto' ? (
                                <>
                                    <Grid item xs={12} sm={4}>
                                        <TextField
                                            select
                                            label="Produto"
                                            fullWidth
                                            value={selectedProductId}
                                            onChange={(e) => {
                                                setSelectedProductId(e.target.value);
                                                setSelectedVariacaoId('');
                                            }}
                                        >
                                            {products.map((p) => (
                                                <MenuItem key={p.id} value={p.id}>
                                                    {p.nome}
                                                </MenuItem>
                                            ))}
                                        </TextField>
                                    </Grid>

                                    <Grid item xs={12} sm={4}>
                                        <TextField
                                            select
                                            label="Variação"
                                            fullWidth
                                            disabled={!selectedProductId}
                                            value={selectedVariacaoId}
                                            onChange={(e) => setSelectedVariacaoId(e.target.value)}
                                        >
                                            {variacoesDoProduto.map((v) => (
                                                <MenuItem key={v.id} value={v.id}>
                                                    {v.nome_variacao} - custo estimado {formatCurrencyBR(v.valor_custo)}
                                                </MenuItem>
                                            ))}
                                        </TextField>
                                    </Grid>
                                </>
                            ) : (
                                <Grid item xs={12} sm={8}>
                                    <TextField
                                        select
                                        label="Serviço"
                                        fullWidth
                                        value={selectedServiceId}
                                        onChange={(e) => setSelectedServiceId(e.target.value)}
                                    >
                                        {services.map((s) => (
                                            <MenuItem key={s.id} value={s.id}>
                                                {s.name} - {formatCurrencyBR(s.price)}
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                </Grid>
                            )}

                            <Grid item xs={8} sm={2}>
                                <TextField
                                    label="Qtd"
                                    type="number"
                                    fullWidth
                                    inputProps={{ min: 1 }}
                                    value={quantidade}
                                    onChange={(e) => setQuantidade(e.target.value)}
                                />
                            </Grid>

                            <Grid item xs={4} sm={2} display="flex" alignItems="center">
                                <Button variant="outlined" startIcon={<IconPlus size={18} />} onClick={addItem} fullWidth>
                                    Adicionar
                                </Button>
                            </Grid>

                            <Grid item xs={12}>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Item</TableCell>
                                            <TableCell align="right">Quantidade</TableCell>
                                            <TableCell align="right">Custo estimado</TableCell>
                                            <TableCell align="right">Subtotal estimado</TableCell>
                                            <TableCell align="right" />
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {items.map((item, index) => (
                                            <TableRow key={index}>
                                                <TableCell>{item.nome}</TableCell>
                                                <TableCell align="right">{item.quantidade}</TableCell>
                                                <TableCell align="right">{formatCurrencyBR(item.valor_unitario_estimado)}</TableCell>
                                                <TableCell align="right">
                                                    {formatCurrencyBR(item.quantidade * item.valor_unitario_estimado)}
                                                </TableCell>
                                                <TableCell align="right">
                                                    <IconButton size="small" onClick={() => removeItem(index)}>
                                                        <IconTrash size={16} />
                                                    </IconButton>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                        {items.length === 0 && (
                                            <TableRow>
                                                <TableCell colSpan={5} align="center">
                                                    Nenhum item adicionado
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </Grid>

                            <Grid item xs={12} textAlign="right">
                                <Typography variant="h5">Total estimado: {formatCurrencyBR(totalEstimado)}</Typography>
                            </Grid>

                            <Grid item xs={12}>
                                <Box display="flex" justifyContent="flex-end" gap={2}>
                                    <Button onClick={() => history.push('/dashboard/purchasing/requests')}>Voltar</Button>
                                    <Button type="submit" variant="contained">
                                        Salvar Solicitação (Rascunho)
                                    </Button>
                                </Box>
                            </Grid>
                        </Grid>
                    </form>
                </CardContent>
            </Card>
        </Box>
    );
};

export default CreatePurchaseRequest;
